import type { z } from "zod";

/**
 * Parse a value using the zod schema, or default. This is useful when parsing build-time
 * environmental variables that need to be access via the full object path, like
 * `process.env.COMMIT`.
 */
// oxlint-disable-next-line typescript/no-explicit-any -- legacy any, may or may not be needed
export function parseOrDefault<SchemaType extends z.ZodType<any, any>>(
  value: string | undefined,
  schema: SchemaType,
  defaultValue: z.infer<SchemaType>,
): z.infer<SchemaType> {
  const parseResult = schema.safeParse(value);

  if (parseResult.success) {
    return parseResult.data;
  }

  return defaultValue;
}

/**
 * Parse an arbitrary value using the zod schema, returning `null` on
 * failure. Use this for the common pattern of validating untrusted
 * runtime data (postMessage payloads, JSON strings parsed out of DOM
 * attributes, etc.) where the caller doesn't care *why* parsing failed
 * — they just want the typed value or nothing.
 */
// oxlint-disable-next-line typescript/no-explicit-any -- Need this any in order to have the generic extend
export function parseSchemaOrNull<SchemaType extends z.ZodType<any, any>>(
  value: unknown,
  schema: SchemaType,
): z.infer<SchemaType> | null {
  const parseResult = schema.safeParse(value);
  return parseResult.success ? parseResult.data : null;
}
